import { useTranslations } from '@vocab/react';
import { Box, Button, Hidden, IconAI } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { sendHotJarEvent } from 'src/hooks/useHotjarEvent';

import translations from '../.vocab';
import { CareerFeedAIAssistantDrawer } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantDrawer/CareerFeedAIAssistantDrawer';
import { CareerFeedAIAssistantWidgetContent } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantWidgetContent';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from '../hooks/useRecommendationForCareerFeedAiAssistant';
import type { ErrorType } from '../types';

import SocialProofSection from './SocialProofSection/SocialProofSection';

const CareerFeedAiAssistantInitialStateView = ({
  isLoading,
  errorType,
  onButtonClick,
}: {
  isLoading: boolean;
  errorType?: ErrorType | null;
  onButtonClick: () => void;
  openDrawer?: boolean;
}) => {
  const { t } = useTranslations(translations);

  const { reset, openDesktopDrawer, setOpenDesktopDrawer } =
    useCareerFeedAIAssistant();

  const handleOnClose = useCallback(() => {
    reset();
    setOpenDesktopDrawer(false);
  }, [reset, setOpenDesktopDrawer]);

  const desktopDrawerButton = (
    <>
      <Hidden below="tablet">
        <Button
          tone="brandAccent"
          icon={<IconAI />}
          loading={false}
          onClick={onButtonClick}
        >
          {t('Refine recommendations')}
        </Button>
      </Hidden>
      <Hidden above="mobile">
        <Button
          tone="brandAccent"
          icon={<IconAI />}
          loading={isLoading}
          onClick={onButtonClick}
        >
          {t('Refine recommendations')}
        </Button>
      </Hidden>
    </>
  );

  return (
    <>
      <CareerFeedAIAssistantWidgetContent
        headMessage={{
          text: t(
            'Answer a few questions so we can find the right jobs for you.',
          ),
          size: 'standard',
        }}
        ctaSection={<Box maxWidth="content">{desktopDrawerButton}</Box>}
        errorType={errorType}
        socialProofSection={<SocialProofSection />}
        isLoading={isLoading && !openDesktopDrawer}
      />
      <CareerFeedAIAssistantDrawer
        contentSection={<Box height="full" paddingY="xxlarge" />}
        shouldOpen={openDesktopDrawer}
        onClose={handleOnClose}
        isLoading={isLoading}
      />
    </>
  );
};

export const CareerFeedAiAssistantInitialState = () => {
  const {
    invokeCreateSession,
    isCreateSessionLoading,
    createSessionError,
    tracking: { trackGenAIChatStartPressed, trackGenAIChatCtaDisplayed },
    setOpenDesktopDrawer,
  } = useCareerFeedAIAssistant();
  const { jobIds: recommendedJobIds } =
    useRecommendationForCareerFeedAiAssistant();

  const startSession = useCallback(() => {
    if (!isCreateSessionLoading) {
      trackGenAIChatStartPressed();
      invokeCreateSession(recommendedJobIds);
    }
    setOpenDesktopDrawer(true);
  }, [
    invokeCreateSession,
    isCreateSessionLoading,
    recommendedJobIds,
    trackGenAIChatStartPressed,
    setOpenDesktopDrawer,
  ]);

  const { ref: trackedComponent, inView: isInView } = useInView({
    delay: 500,
    threshold: 0.5,
  });
  const [isCtaDisplayedEventFired, setIsCtaDisplayedEventFired] =
    useState(false);

  useEffect(() => {
    if (isInView && !isCtaDisplayedEventFired) {
      trackGenAIChatCtaDisplayed();
      sendHotJarEvent('homepage_genAI_start');
      setIsCtaDisplayedEventFired(true);
    }
  }, [isCtaDisplayedEventFired, isInView, trackGenAIChatCtaDisplayed]);

  return (
    <Box ref={trackedComponent}>
      <CareerFeedAiAssistantInitialStateView
        isLoading={isCreateSessionLoading}
        errorType={createSessionError}
        onButtonClick={startSession}
      />
    </Box>
  );
};
